import { React, cloneElement, forwardRef, useEffect, useState } from 'react';

// mui
import {
    Grid,
    Paper,
    Autocomplete,
    TableContainer,
    Table,
    TableRow,
    TableBody,
    TableCell,
    Box,
    Typography,
    TextField,
} from '@mui/material';

// components
import {
    optionsFilterType,
    optionsFilterSelection,
    optionsFilterWidth,
    optionsFilterGroup,
    cloneObject,
    getFilterStatusString,
} from '../..';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ListboxComponent, ContainerScroll } from '@goldblack/client-comps';

function UpdateFilter(filterId, field, data, filters) {
    let found = filters.data.find((type) => type._id === filterId);
    let newType = cloneObject(found);

    newType[field] = data;

    let callback = (type) => type._id === filterId;
    storage.transience.updateState('loise.filter.data', callback, newType);
}

function LoiseFilter(props) {
    const filter = storage.transience.getValue('loise.filter');

    const onChangeFilter = (event, value) => {
        const filterValue = value ? value : '';
        UpdateFilter(props.filter._id, 'type', filterValue, filter);
    };

    return (
        <Autocomplete
            sx={{ m: 0, p: 0 }}
            options={optionsFilterType}
            value={optionsFilterType.find((x) => x === props.filter.type)}
            autoHighlight
            slotProps={{
                listbox: {
                    component: ListboxComponent,
                },
            }}
            renderInput={(params) => <TextField {...params} size='small' />}
            onChange={onChangeFilter}
        />
    );
}

function LoiseFilterInputOption(props) {
    const filter = storage.transience.getValue('loise.filter');

    const OptionOption = (props) => {
        const onChangeOption = (event, value) => {
            const filterValue = value ? value.id : '';
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].optionId = filterValue;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Autocomplete
                size='small'
                sx={{ width: '100%' }}
                options={props.options}
                getOptionLabel={(option) => option.code}
                defaultValue={props.options.find((x) => x.id === props.option.optionId)}
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                    paper: {
                        variant: 'outlined',
                    },
                }}
                renderOption={(props, option) => (
                    <Box component='li' {...props} key={option.id}>
                        <Typography sx={{ fontSize: '14px' }}>{option.code}</Typography>
                    </Box>
                )}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeOption}
            />
        );
    };

    const OptionRenderDisplay = (props) => {
        const onChangeDisplay = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].renderDisplay = filterValue;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeDisplay}
                variant='outlined'>
                {props.option.renderDisplay}
            </Paper>
        );
    };

    const OptionOptionLabel = (props) => {
        const onChangeLabel = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].optionLabel = filterValue;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeLabel}
                variant='outlined'>
                {props.option.optionLabel}
            </Paper>
        );
    };

    const OptionResultValue = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].resultValue = filterValue;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultValue}
            </Paper>
        );
    };

    const OptionResultDisplay = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].resultDisplay = filterValue;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultDisplay}
            </Paper>
        );
    };

    const OptionWidth = (props) => {
        const onChangeWidth = (event, value) => {
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].width = value ? parseFloat(value) : 0;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterWidth}
                defaultValue={optionsFilterWidth.find((x) => x === props.option.width.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeWidth}
            />
        );
    };

    const OptionSelection = (props) => {
        const onChangeSelection = (event, value) => {
            let options = cloneObject(props.filter.inputOptions);
            options[props.index].selection = value ? parseInt(value) : 0;
            UpdateFilter(props.filter._id, 'inputOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterSelection}
                defaultValue={optionsFilterSelection.find((x) => x === props.option.selection.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeSelection}
            />
        );
    };

    return (
        <TableContainer>
            <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>optionId</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            <OptionOption {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>width</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionWidth {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>renderDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionRenderDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>optionLabel</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionOptionLabel {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultValue</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultValue {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>selection</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionSelection {...props} />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseFilterSearchOption(props) {
    const filter = storage.transience.getValue('loise.filter');

    const OptionOption = (props) => {
        const onChangeOption = (event, value) => {
            const filterValue = value ? value.id : '';
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].optionId = filterValue;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Autocomplete
                size='small'
                sx={{ width: '100%' }}
                options={props.options}
                getOptionLabel={(option) => option.code}
                defaultValue={props.options.find((x) => x.id === props.option.optionId)}
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                    paper: {
                        variant: 'outlined',
                    },
                }}
                renderOption={(props, option) => (
                    <Box component='li' {...props} key={option.id}>
                        <Typography sx={{ fontSize: '14px' }}>{option.code}</Typography>
                    </Box>
                )}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeOption}
            />
        );
    };

    const OptionWidth = (props) => {
        const onChangeWidth = (event, value) => {
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].width = value ? parseFloat(value) : 0;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterWidth}
                defaultValue={optionsFilterWidth.find((x) => x === props.option.width.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeWidth}
            />
        );
    };

    const OptionRenderDisplay = (props) => {
        const onChangeDisplay = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].renderDisplay = filterValue;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeDisplay}
                variant='outlined'>
                {props.option.renderDisplay}
            </Paper>
        );
    };

    const OptionOptionLabel = (props) => {
        const onChangeLabel = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].optionLabel = filterValue;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeLabel}
                variant='outlined'>
                {props.option.optionLabel}
            </Paper>
        );
    };

    const OptionResultValue = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].resultValue = filterValue;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultValue}
            </Paper>
        );
    };

    const OptionResultDisplay = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].resultDisplay = filterValue;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultDisplay}
            </Paper>
        );
    };

    const OptionSelection = (props) => {
        const onChangeSelection = (event, value) => {
            let options = cloneObject(props.filter.searchOptions);
            options[props.index].selection = value ? parseInt(value) : 0;
            UpdateFilter(props.filter._id, 'searchOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterSelection}
                defaultValue={optionsFilterSelection.find((x) => x === props.option.selection.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeSelection}
            />
        );
    };

    return (
        <TableContainer>
            <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>option</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            <OptionOption {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>width</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionWidth {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>renderDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionRenderDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>optionLabel</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionOptionLabel {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultValue</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultValue {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>selection</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionSelection {...props} />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseFilterAdminOption(props) {
    const filter = storage.transience.getValue('loise.filter');

    const OptionOption = (props) => {
        const onChangeOption = (event, value) => {
            const filterValue = value ? value.id : '';
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].optionId = filterValue;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Autocomplete
                size='small'
                sx={{ width: '100%' }}
                options={props.options}
                getOptionLabel={(option) => option.code}
                defaultValue={props.options.find((x) => x.id === props.option.optionId)}
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                    paper: {
                        variant: 'outlined',
                    },
                }}
                renderOption={(props, option) => (
                    <Box component='li' {...props} key={option.id}>
                        <Typography sx={{ fontSize: '14px' }}>{option.code}</Typography>
                    </Box>
                )}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeOption}
            />
        );
    };

    const OptionWidth = (props) => {
        const onChangeWidth = (event, value) => {
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].width = value ? parseFloat(value) : 0;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterWidth}
                defaultValue={optionsFilterWidth.find((x) => x === props.option.width.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeWidth}
            />
        );
    };

    const OptionRenderDisplay = (props) => {
        const onChangeDisplay = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].renderDisplay = filterValue;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeDisplay}
                variant='outlined'>
                {props.option.renderDisplay}
            </Paper>
        );
    };

    const OptionOptionLabel = (props) => {
        const onChangeLabel = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].optionLabel = filterValue;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeLabel}
                variant='outlined'>
                {props.option.optionLabel}
            </Paper>
        );
    };

    const OptionResultValue = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].resultValue = filterValue;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultValue}
            </Paper>
        );
    };

    const OptionResultDisplay = (props) => {
        const onChangeResult = (event, value) => {
            const filterValue = event.currentTarget.innerText;
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].resultDisplay = filterValue;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Paper
                sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
                suppressContentEditableWarning={true}
                contentEditable={true}
                onBlur={onChangeResult}
                variant='outlined'>
                {props.option.resultDisplay}
            </Paper>
        );
    };

    const OptionSelection = (props) => {
        const onChangeSelection = (event, value) => {
            let options = cloneObject(props.filter.adminOptions);
            options[props.index].selection = value ? parseInt(value) : 0;
            UpdateFilter(props.filter._id, 'adminOptions', options, filter);
        };
        return (
            <Autocomplete
                sx={{ m: 0, p: 0 }}
                options={optionsFilterSelection}
                defaultValue={optionsFilterSelection.find((x) => x === props.option.selection.toString())}
                autoHighlight
                slotProps={{
                    listbox: {
                        component: ListboxComponent,
                    },
                }}
                renderInput={(params) => <TextField {...params} size='small' />}
                onChange={onChangeSelection}
            />
        );
    };

    return (
        <TableContainer>
            <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                <TableBody>
                    <TableRow>
                        <TableCell sx={{ width: '20%', borderBottom: 'none' }}>option</TableCell>
                        <TableCell sx={{ width: '80%', borderBottom: 'none' }} align='right'>
                            <OptionOption {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>width</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionWidth {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>renderDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionRenderDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>optionLabel</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionOptionLabel {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultValue</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultValue {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>resultDisplay</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionResultDisplay {...props} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell sx={{ borderBottom: 'none' }}>selection</TableCell>
                        <TableCell sx={{ borderBottom: 'none' }} align='right'>
                            <OptionSelection {...props} />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function LoiseFilterGroup(props) {
    const filter = storage.transience.getValue('loise.filter');

    const onChangeGroup = (event, values) => {
        const flags = values.reduce((acc, v) => acc | v.code, 0);
        UpdateFilter(props.filter._id, 'groupFlags', flags, filter);
    };

    const selected = optionsFilterGroup.filter((o) => (props.filter.groupFlags & o.code) !== 0);

    return (
        <Autocomplete
            sx={{ m: 0, p: 0 }}
            multiple
            options={optionsFilterGroup}
            value={selected}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            disableCloseOnSelect
            slotProps={{
                listbox: { component: ListboxComponent },
            }}
            renderInput={(params) => <TextField {...params} size='small' />}
            onChange={onChangeGroup}
        />
    );
}

function LoiseFilterName(props) {
    const filter = storage.transience.getValue('loise.filter');

    const changeName = (event) => {
        UpdateFilter(props.filter._id, 'name', event.currentTarget.innerText, filter);
    };

    return (
        <Paper
            sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
            suppressContentEditableWarning={true}
            contentEditable={true}
            onBlur={changeName}
            elevation={0}>
            {props.filter ? props.filter.name : ''}
        </Paper>
    );
}

function LoiseFilterLabel(props) {
    const filter = storage.transience.getValue('loise.filter');

    const changeLabel = (event) => {
        UpdateFilter(props.filter._id, 'label', event.currentTarget.innerText, filter);
    };

    return (
        <Paper
            sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
            suppressContentEditableWarning={true}
            contentEditable={true}
            onBlur={changeLabel}
            elevation={0}>
            {props.filter ? (props.filter.label ?? '') : ''}
        </Paper>
    );
}

function LoiseFilterDisplay(props) {
    const filter = storage.transience.getValue('loise.filter');

    const changeDisplay = (event) => {
        UpdateFilter(props.filter._id, 'inputDisplay', event.currentTarget.innerText, filter);
    };

    return (
        <Paper
            sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
            suppressContentEditableWarning={true}
            contentEditable={true}
            onBlur={changeDisplay}
            elevation={0}>
            {props.filter ? props.filter.inputDisplay : ''}
        </Paper>
    );
}

function LoiseFilterSearchDisplay(props) {
    const filter = storage.transience.getValue('loise.filter');

    const changeDisplay = (event) => {
        UpdateFilter(props.filter._id, 'searchDisplay', event.currentTarget.innerText, filter);
    };

    return (
        <Paper
            sx={{ backgroundColor: 'background.default', px: 1, py: 0.5 }}
            suppressContentEditableWarning={true}
            contentEditable={true}
            onBlur={changeDisplay}
            elevation={0}>
            {props.filter ? props.filter.searchDisplay : ''}
        </Paper>
    );
}

function LoiseFiltersFilterInfoPanel(props) {
    const [createdString, setCreatedString] = useState('');
    const [modifiedString, setModifiedString] = useState('');

    useEffect(() => {
        if (props.filter) {
            setCreatedString(props.filter.createdAt ? new Date(props.filter.createdAt).toLocaleString('ko-KR') : '');
            setModifiedString(props.filter.modifiedAt ? new Date(props.filter.modifiedAt).toLocaleString('ko-KR') : '');
        }
    }, [props.filter]);

    return (
        <ContainerScroll>
            <TableContainer>
                <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ width: '30%' }}>name</TableCell>
                            <TableCell sx={{ width: '70%' }} align='right'>
                                <LoiseFilterName {...props} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>label</TableCell>
                            <TableCell align='right'>
                                <LoiseFilterLabel {...props} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>type</TableCell>
                            <TableCell align='right'>
                                <LoiseFilter {...props} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>status</TableCell>
                            <TableCell align='right'>{getFilterStatusString(props.filter.status)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>createdAt</TableCell>
                            <TableCell align='right'>{createdString}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>modifiedAt</TableCell>
                            <TableCell align='right'>{modifiedString}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>groupFlags</TableCell>
                            <TableCell align='right'>
                                <LoiseFilterGroup {...props} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>inputDisplay</TableCell>
                            <TableCell align='right'>
                                <LoiseFilterDisplay {...props} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>searchDisplay</TableCell>
                            <TableCell align='right'>
                                <LoiseFilterSearchDisplay {...props} />
                            </TableCell>
                        </TableRow>
                        {props.filter.inputOptions.map((option, index) => (
                            <TableRow key={`inputOption ${index}`}>
                                <TableCell>{`inputOption ${index}`}</TableCell>
                                <TableCell padding='none' align='right'>
                                    <LoiseFilterInputOption {...props} option={option} index={index} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {props.filter.searchOptions.map((option, index) => (
                            <TableRow key={`searchOption ${index}`}>
                                <TableCell>{`searchOption ${index}`}</TableCell>
                                <TableCell padding='none' align='right'>
                                    <LoiseFilterSearchOption {...props} option={option} index={index} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {props.filter.adminOptions.map((option, index) => (
                            <TableRow key={`adminOption ${index}`}>
                                <TableCell>{`adminOption ${index}`}</TableCell>
                                <TableCell padding='none' align='right'>
                                    <LoiseFilterAdminOption {...props} option={option} index={index} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </ContainerScroll>
    );
}

export { LoiseFiltersFilterInfoPanel };
